import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Settings {
  showCliCommands: boolean;
  theme: 'dark' | 'light';
}

interface SettingsContextType {
  settings: Settings;
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  toggleCliCommands: () => void;
}

const defaultSettings: Settings = {
  showCliCommands: false,
  theme: 'dark',
};

const STORAGE_KEY = 'estream-console-settings';

const SettingsContext = createContext<SettingsContextType | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaultSettings, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.warn('Failed to load settings:', e);
    }
    return defaultSettings;
  });

  // Persist settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save settings:', e);
    }
  }, [settings]);

  const updateSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCliCommands = () => {
    setSettings((prev) => ({ ...prev, showCliCommands: !prev.showCliCommands }));
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSetting, toggleCliCommands }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export function useShowCliCommands() {
  const { settings } = useSettings();
  return settings.showCliCommands;
}
